/**
 * sanitizeHtml — central XSS hardening for user-authored HTML rendered to viewers.
 *
 * WHY THIS EXISTS
 * ---------------
 * Wheel rich-text (Froala/TinyMCE output), Button labels, the profile "About"
 * bio, and the Business Box title/description/CTA labels are stored as raw HTML
 * and rendered to OTHER users via `<FroalaEditorView>` / `dangerouslySetInnerHTML`
 * with NO sanitization. Because wheels are public, cloneable and embeddable, a
 * crafted `<img onerror>`, `<script>`, `javascript:` href, etc. is a STORED XSS
 * that fires in every viewer's session (audit findings B-10 / B-18 + the EW XSS
 * cluster).
 *
 * SAFETY MODEL (read before changing)
 * -----------------------------------
 * The iYuga app is LIVE and fragile. This module is ADDITIVE and FLAG-GATED:
 *
 *   - Default behaviour is UNCHANGED. `REACT_APP_SANITIZE_HTML` is OFF by
 *     default, so `sanitizeUserHtml()` returns its input verbatim (today's raw
 *     render). Nothing in production changes until the flag is flipped to
 *     "true" — and even then, only the render output of the wired sinks changes.
 *   - When ON, we run DOMPurify with a Froala-aware allow-list that PRESERVES
 *     legitimate rich-text formatting (headings, lists, links, inline color /
 *     font-family / font-size styles, images) while stripping the XSS vectors
 *     (`<script>`, `<iframe>` unless explicitly opted in, `on*` event handlers,
 *     `javascript:` / `data:` URLs, etc.).
 *
 * The flag name follows the repo convention (`REACT_APP_*` read as
 * `=== "true"`, e.g. `REACT_APP_RECAPTCHA_ENABLED`).
 *
 * ROLLBACK: unset/blank `REACT_APP_SANITIZE_HTML` (or set it to anything other
 * than "true") and rebuild — behaviour reverts to the pre-fix raw render. No
 * code revert required.
 */

import DOMPurify from "dompurify";

/**
 * Feature flag — DEFAULT OFF.
 * Only the exact string "true" enables sanitization, matching the repo's
 * existing `process.env.REACT_APP_* === "true"` convention.
 */
export const SANITIZE_HTML_ENABLED =
  process.env.REACT_APP_SANITIZE_HTML === "true";

/**
 * Froala / TinyMCE legitimately emit inline styles (text color, font-family,
 * font-size, alignment) and a rich set of formatting tags. We keep those so the
 * fix is invisible to honest content, and only strip the dangerous surface.
 *
 * DOMPurify already strips `on*` handlers, `<script>`, and `javascript:` URLs
 * by default; the config below tightens / documents that posture and keeps the
 * formatting tags + the `style` attribute that Froala relies on.
 */
const BASE_CONFIG = {
  // Keep the formatting tags Froala/TinyMCE produce. DOMPurify's default
  // allow-list already covers these, but we pin the security-relevant knobs.
  ALLOWED_ATTR: [
    // formatting / layout
    "style",
    "class",
    "align",
    "dir",
    "lang",
    "title",
    // links
    "href",
    "target",
    "rel",
    // media
    "src",
    "alt",
    "width",
    "height",
    "loading",
    // tables
    "colspan",
    "rowspan",
    "scope",
    // data-* used by Froala for layout hints (safe: not executable)
    "data-fr-image-pasted",
  ],
  // Never allow these regardless of context.
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input", "base", "link", "meta"],
  FORBID_ATTR: ["srcdoc", "formaction", "xlink:href"],
  // Restrict URL schemes to http(s), mailto, tel, callto, sms, relative paths,
  // and anchors. javascript:/vbscript:/file: are rejected by this allow-list.
  //
  // NOTE: this regexp alone does NOT block data: URIs on media tags — DOMPurify
  // treats <img>/<audio>/<video>/<source> as "data-URI-safe" and allows data:
  // on them regardless of ALLOWED_URI_REGEXP. Because a data: URI can smuggle an
  // executable SVG/HTML payload, we fail closed on ALL data: URIs via the
  // uponSanitizeAttribute hook in ensureHooks() below (not here).
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel|callto|sms):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  // Keep the content of removed elements (so stripping <script> doesn't nuke
  // surrounding text). DOMPurify default; pinned for clarity.
  KEEP_CONTENT: true,
  // We render trusted-by-allow-list HTML, not full documents.
  WHOLE_DOCUMENT: false,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
};

/**
 * One-time hardening hook: force every link that opens in a new tab to carry
 * rel="noopener noreferrer" (reverse-tabnabbing defence). Registered lazily so
 * importing this module has no side effects when the flag is OFF.
 */
let hookRegistered = false;
function ensureHooks() {
  if (hookRegistered || typeof DOMPurify.addHook !== "function") return;

  // Fail closed on data: URIs in ANY attribute (src, href, etc.). DOMPurify
  // otherwise allows data: on media tags (img/audio/video/source), which can
  // smuggle an executable data:image/svg+xml payload. We normalise the value
  // (strip whitespace + control chars, lower-case) before matching so obfuscated
  // variants like "da ta:" or "DATA:" or "\tdata:" are also dropped.
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    const normalized = (data.attrValue || "")
      .replace(/[\u0000-\u0020\s]+/g, "")
      .toLowerCase();
    if (normalized.startsWith("data:")) {
      data.keepAttr = false;
    }
  });

  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    // Case-insensitive tagName check: HTML-mode DOMPurify normalises to
    // uppercase, but guard defensively for parsers that emit lowercase.
    if (node.tagName?.toUpperCase() === "A" && node.getAttribute("target")) {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  hookRegistered = true;
}

/**
 * Sanitize user-authored rich-text / HTML for safe rendering.
 *
 * @param {string} dirty - raw HTML string from user content (may be null/undefined).
 * @param {object} [options]
 * @param {boolean} [options.allowIframe=false] - allow <iframe> (e.g. trusted
 *        Spotify/SoundCloud embeds). Only set true where the source is already
 *        validated upstream.
 * @returns {string} sanitized HTML when the flag is ON; the original string
 *        unchanged when OFF (default).
 */
export function sanitizeUserHtml(dirty, options = {}) {
  // Flag OFF → behaviour identical to today (raw passthrough). This is the
  // load-bearing safety guarantee: nothing changes in prod until the flag flips.
  if (!SANITIZE_HTML_ENABLED) return dirty;

  // Nothing to do for empty/non-string input.
  if (dirty == null) return dirty;
  if (typeof dirty !== "string") return dirty;
  if (dirty === "") return dirty;

  ensureHooks();

  const config = { ...BASE_CONFIG };
  if (options.allowIframe) {
    config.FORBID_TAGS = BASE_CONFIG.FORBID_TAGS.filter((t) => t !== "iframe");
    config.ADD_TAGS = ["iframe"];
    config.ADD_ATTR = ["allow", "allowfullscreen", "frameborder", "scrolling"];
  }

  return DOMPurify.sanitize(dirty, config);
}

export default sanitizeUserHtml;
